"use client";

import { FC, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { captureException } from "@sentry/react";
import GalleryImage from "components/atoms/GalleryImage";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import SearchAccordion from "components/molecules/SearchAccordion";
import ShareModal from "components/molecules/ShareModal";
import { deleteImage, getFilterFields, searchImages,
// updated to use a standard paginated fetch
voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, FilterFieldDTO, ImageDTO, ImageSearchQueryDTO } from "types";
import { Label, SubTitle } from "utils/styling/typography.styled";
import * as Styled from "./CompetitionGallery.styled";
import Masonry from "react-masonry-css";

/**
 * Updated CompetitionGallery to use simple pagination
 * instead of infinite scrolling.
 * We keep track of the current page, total images, page size, etc.
 * Then we provide navigation controls for the user to go back and forth.
 */

type CompetitionGalleryProps = {
  gallerySubtitle: string;
  userGallery: boolean;
  defaultFilters?: {
    [key: string]: string;
  };
  doAction: (imageId: string) => void;
  createdAtBefore?: string;
};
export const CompetitionGallery: FC<CompetitionGalleryProps> = ({
  gallerySubtitle,
  userGallery,
  defaultFilters,
  doAction,
  createdAtBefore
}) => {
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [sort, setSort] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterFields, setFilterFields] = useState<FilterFieldDTO[]>([]);
  const [filters, setFilters] = useState<any>(defaultFilters || {});
  const [query, setQuery] = useState("");
  const [imageId, setImageId] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(15);
  const [totalImages, setTotalImages] = useState<number>(0);
  const handleCompetitionsClick = (imageId: string) => {
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const data = imagesQuery?.data;
    const allImages = data?.images ?? [];
    const selectedImage = allImages.find(image => image?.id === imageId);
    if (selectedImage) {
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await deleteImage(imageToDelete);

      // Remove deleted image from cache
      queryClient.invalidateQueries(["images", sort, sortOrder, filters, query, userGallery, createdAtBefore, currentPage, pageSize]);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const data = imagesQuery?.data;
    const allImages = data?.images ?? [];
    const currentImage = allImages.find(img => img?.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.votedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      queryClient.invalidateQueries(["images", sort, sortOrder, filters, query, userGallery, createdAtBefore, currentPage, pageSize]);
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  useEffect(() => {
    const fetchFilters = async () => {
      const fields = await getFilterFields();
      setFilterFields(fields);
    };
    fetchFilters();
  }, []);
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = event.target as HTMLInputElement | HTMLSelectElement;
    const checked = type === "checkbox" ? (event.target as HTMLInputElement).checked : false;
    setFilters((prevFilters: any) => ({
      ...prevFilters,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // We now use a standard useQuery to load images by page
  const fetchPaginatedImages = async () => {
    const searchQuery: ImageSearchQueryDTO = {
      query,
      sortField: sort,
      sortOrder,
      filters,
      page: currentPage,
      pageSize,
      userGallery,
      createdAfter: createdAtBefore ? new Date(createdAtBefore).toISOString() : null
    };
    // searchImages now returns an object with { images: ImageDTO[], totalCount: number }
    const response = await searchImages(searchQuery);
    if (response.error) {
      throw new Error(response.error.toString());
    }
    // set total images from response
    setTotalImages(response.totalCount || 0);
    return response;
  };
  const imagesQuery = useQuery(["images", sort, sortOrder, filters, query, userGallery, createdAtBefore, currentPage, pageSize], fetchPaginatedImages);
  const images: ImageDTO[] = imagesQuery?.data?.images ?? [];
  const isLoading = imagesQuery?.isLoading;
  const isError = imagesQuery?.isError;
  const handleSelectImage = (id: string) => {
    setImageId(id);
    doAction(id);
  };
  const noOp = () => {};
  const isUserImage = (imageUsername: string) => imageUsername === "user";
  const isSelected = (selectedImageId: string) => selectedImageId === imageId;
  const renderImage = (image: ImageDTO) => <GalleryImage key={image.id} item={image} onImageClick={() => handleSelectImage(image.id)} isUserImage={isUserImage} handleToggle={noOp} handleEditClick={noOp} handleADetailerClick={noOp} handleFaceSwapClick={noOp} handleBGRemovalClick={noOp} handleInpaintClick={noOp} handleUpscaleClick={noOp} handleAudioClick={noOp} prepareForDelete={prepareForDelete} handleVote={handleVote} isSelectionMode={false} handleSelectImage={handleSelectImage} isSelected={isSelected(image.id)} userGallery={userGallery} handleShareClick={handleShareClick} showIcons={false} showDeleteConfirmation={showDeleteConfirmation} handleCompetitionsClick={() => handleCompetitionsClick(image.id)} data-sentry-element="GalleryImage" data-sentry-component="renderImage" data-sentry-source-file="index.tsx" />;

  // Pagination helpers
  const totalPages = Math.ceil(totalImages / pageSize);
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0); // reset to first page
  };
  return <Styled.ImageGalleryWrapper data-sentry-element="unknown" data-sentry-component="CompetitionGallery" data-sentry-source-file="index.tsx">
            <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">{gallerySubtitle}</SubTitle>
            <SearchAccordion filterFields={filterFields} filters={filters} handleFilterChange={handleFilterChange} query={query} setQuery={setQuery} sort={sort} setSort={setSort} sortOrder={sortOrder} setSortOrder={setSortOrder} pageSize={pageSize} handleChangePageSize={handleChangePageSize} currentPage={currentPage} totalPages={totalPages} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} disabledFilters={Object.keys(defaultFilters || {})} data-sentry-element="SearchAccordion" data-sentry-source-file="index.tsx">
                {/* Gallery content */}
                {isLoading && <Label>Loading ...</Label>}
                {isError && <Label>Error loading images.</Label>}
                {!isLoading && !isError && images.length === 0 && <Label>No images to display</Label>}
                <Masonry breakpointCols={{
        default: 6,
        1280: 4,
        1024: 3,
        768: 2,
        576: 1
      }} className="flex w-auto -ml-4" columnClassName="pl-4 bg-clip-padding" data-sentry-element="Masonry" data-sentry-source-file="index.tsx">
                    {images.filter((image): image is ImageDTO => image !== undefined).map(renderImage)}
                </Masonry>
            </SearchAccordion>

            <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} data-sentry-element="EligibleCompetitionsModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <ShareModal isOpen={isShareModalOpen} image={images.find(img => img?.id === imageId) || null} onClose={handleCloseShareModal} data-sentry-element="ShareModal" data-sentry-source-file="index.tsx" />
        </Styled.ImageGalleryWrapper>;
};
export default CompetitionGallery;